/**
 * ComparisonGrid
 *
 * Main WIP comparison table: Staff Name, Position, Client/Engagement,
 * Scheduled Hours, Actual Hours, Variance (Hrs), WIP Cost Variance ($),
 * Variance Status Badge, Actions. Supports mouse hold-and-drag horizontal
 * scroll and a right-click context menu per row.
 */
import { useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { VarianceStatusBadge } from './VarianceStatusBadge.jsx';
import { RowContextMenu } from './RowContextMenu.jsx';

/**
 * @typedef {object} ComparisonRow
 * @property {string} id
 * @property {string} staffName
 * @property {string} position
 * @property {string} clientName
 * @property {string} engagementId
 * @property {string} engagementName
 * @property {number} scheduledHours
 * @property {number} actualHours
 * @property {number} hourVariance
 * @property {number} costVariance
 * @property {import('./VarianceStatusBadge.jsx').VarianceStatus} status
 */

const currency = (n) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/**
 * @param {{
 *   rows: ComparisonRow[],
 *   isLoading: boolean,
 *   onViewAuditHistory: (row: ComparisonRow) => void,
 *   onDrillDownTimesheet: (row: ComparisonRow) => void,
 *   onDrillDownSchedule: (row: ComparisonRow) => void,
 *   onShowInEngagementView: (row: ComparisonRow) => void,
 * }} props
 */
export const ComparisonGrid = ({
  rows,
  isLoading,
  onViewAuditHistory,
  onDrillDownTimesheet,
  onDrillDownSchedule,
  onShowInEngagementView,
}) => {
  const scrollRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const dragState = useRef({ isDragging: false, startX: 0, startScrollLeft: 0 });
  const [contextMenu, setContextMenu] = useState(
    /** @type {{ x: number, y: number, row: ComparisonRow } | null} */ (null)
  );

  const handleMouseDown = (/** @type {import('react').MouseEvent} */ e) => {
    if (!scrollRef.current) return;
    dragState.current = {
      isDragging: true,
      startX: e.pageX,
      startScrollLeft: scrollRef.current.scrollLeft,
    };
  };

  const handleMouseMove = (/** @type {import('react').MouseEvent} */ e) => {
    if (!dragState.current.isDragging || !scrollRef.current) return;
    const delta = e.pageX - dragState.current.startX;
    scrollRef.current.scrollLeft = dragState.current.startScrollLeft - delta;
  };

  const stopDragging = () => {
    dragState.current.isDragging = false;
  };

  /**
   * @param {import('react').MouseEvent} e
   * @param {ComparisonRow} row
   */
  const handleContextMenu = (e, row) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, row });
  };

  const columns = [
    'Staff Name',
    'Position',
    'Client / Engagement',
    'Scheduled Hrs',
    'Actual Hrs',
    'Variance (Hrs)',
    'WIP Cost Variance',
    'Status',
    'Actions',
  ];

  return (
    <div className="rounded-lg border border-edge bg-surface">
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
        className="cursor-grab overflow-x-auto active:cursor-grabbing"
      >
        <table className="w-full min-w-[960px] table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              {columns.map((col) => (
                <th key={col} className="whitespace-nowrap px-4 py-3 font-medium">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-edge/60">
                  <td colSpan={columns.length} className="px-4 py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-surface-hover" />
                  </td>
                </tr>
              ))}

            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted">
                  No comparison rows for the current filters.
                </td>
              </tr>
            )}

            {!isLoading &&
              rows.map((row) => (
                <tr
                  key={row.id}
                  onContextMenu={(e) => handleContextMenu(e, row)}
                  className="border-b border-edge/60 text-primary hover:bg-surface-hover/40"
                >
                  <td className="whitespace-nowrap px-4 py-3">{row.staffName}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted">{row.position}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{row.clientName}</p>
                    <p className="text-xs text-muted">{row.engagementName}</p>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{row.scheduledHours.toLocaleString()}</td>
                  <td className="whitespace-nowrap px-4 py-3">{row.actualHours.toLocaleString()}</td>
                  <td
                    className={`whitespace-nowrap px-4 py-3 font-medium ${
                      row.hourVariance > 0
                        ? 'text-amber-400'
                        : row.hourVariance < 0
                          ? 'text-accent'
                          : 'text-secondary'
                    }`}
                  >
                    {row.hourVariance > 0 ? '+' : ''}
                    {row.hourVariance.toLocaleString()}
                  </td>
                  <td
                    className={`whitespace-nowrap px-4 py-3 font-medium ${
                      row.costVariance > 0 ? 'text-amber-400' : 'text-accent'
                    }`}
                  >
                    {currency(row.costVariance)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <VarianceStatusBadge status={row.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <button
                      type="button"
                      onClick={(e) => handleContextMenu(e, row)}
                      className="rounded p-1 text-muted hover:bg-surface-hover hover:text-primary"
                      aria-label={`Actions for ${row.staffName}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {contextMenu && (
        <RowContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onViewAuditHistory={() => onViewAuditHistory(contextMenu.row)}
          onDrillDownTimesheet={() => onDrillDownTimesheet(contextMenu.row)}
          onDrillDownSchedule={() => onDrillDownSchedule(contextMenu.row)}
          onShowInEngagementView={() => onShowInEngagementView(contextMenu.row)}
        />
      )}
    </div>
  );
};
